import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const DEPRECIATION_METHODS = ["Straight-Line", "WDV", "None"];
const STATUS_OPTIONS = ["Available", "Allocated", "In Repair", "Disposed", "In Warehouse"];
const LOCATIONS = ["Head Office", "Branch Office", "Remote", "Warehouse", "Client Site"];
const DEPARTMENTS = ["IT", "HR", "Finance", "Operations", "Sales", "Marketing", "Admin", "Engineering", "Legal"];

const initialForm = {
  category: "",
  subCategory: "",
  assetName: "",
  brand: "",
  modelNumber: "",
  serialNumber: "",
  colour: "",
  purchaseDate: "",
  purchaseCost: "",
  vendorId: "",
  vendorName: "",
  poNumber: "",
  grnNumber: "",
  warrantyStartDate: "",
  warrantyEndDate: "",
  location: "",
  department: "",
  status: "Available",
  depreciationMethod: "Straight-Line",
  usefulLifeYears: "",
  residualValue: "",
  remarks: "",
};

export default function NewAsset() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialForm);
  const [categoryFields, setCategoryFields] = useState({});
  const [vendors, setVendors] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");
  

  // Dynamic master data from API
  const [categories, setCategories] = useState([]);
  const [dynamicSubCategories, setDynamicSubCategories] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]);

  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) {
      toast.error("You must be logged in.");
      navigate("/");
      return;
    }
    // if (!roles.includes("ROLE_ADMIN")) {
    //   toast.error("Unauthorized.");
    //   navigate("/");
    // }
  }, [navigate, token]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors?status=Active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setVendors(Array.isArray(data) ? data : data.content || []);
      } catch {
        setVendors([]);
      }
    };
    fetchVendors();
  }, [token]);

  // Fetch all categories on mount
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/asset-masters/categories`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, [token]);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (!formData.category) { setDynamicSubCategories([]); return; }
    const cat = categories.find((c) => c.name === formData.category);
    if (!cat) return;
    fetch(`${API_BASE_URL}/api/asset-masters/subcategories?categoryId=${cat.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setDynamicSubCategories(Array.isArray(data) ? data : []))
      .catch(() => setDynamicSubCategories([]));
  }, [formData.category, categories, token]);

  // Fetch dynamic fields when subcategory changes
  useEffect(() => {
    if (!formData.subCategory) { setDynamicFields([]); return; }
    fetch(
      `${API_BASE_URL}/api/asset-masters/fields/for-form?subCategoryName=${encodeURIComponent(formData.subCategory)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((data) => setDynamicFields(Array.isArray(data) ? data : []))
      .catch(() => setDynamicFields([]));
  }, [formData.subCategory, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") {
      setFormData((prev) => ({ ...prev, category: value, subCategory: "" }));
      setCategoryFields({});
      return;
    }
    if (name === "vendorId") {
      const selectedVendor = vendors.find((vendor) => String(vendor.id) === value);
      setFormData((prev) => ({
        ...prev,
        vendorId: value,
        vendorName: selectedVendor?.name || "",
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.category) nextErrors.category = "Category is required.";
    if (!formData.subCategory) nextErrors.subCategory = "Sub-category is required.";
    if (!formData.assetName.trim()) nextErrors.assetName = "Asset name is required.";
    if (!formData.brand.trim()) nextErrors.brand = "Brand is required.";
    if (!formData.modelNumber.trim()) nextErrors.modelNumber = "Model number is required.";
    if (!formData.serialNumber.trim()) nextErrors.serialNumber = "Serial number is required.";
    if (!formData.purchaseDate) nextErrors.purchaseDate = "Purchase date is required.";
    if (!formData.purchaseCost || Number(formData.purchaseCost) <= 0) nextErrors.purchaseCost = "Purchase cost must be greater than 0.";
    if (!formData.warrantyStartDate) nextErrors.warrantyStartDate = "Warranty start date is required.";
    if (!formData.warrantyEndDate) nextErrors.warrantyEndDate = "Warranty end date is required.";
    if (formData.warrantyStartDate && formData.warrantyEndDate && formData.warrantyEndDate < formData.warrantyStartDate) {
      nextErrors.warrantyEndDate = "Warranty end date must be after start date.";
    }
    if (!formData.location) nextErrors.location = "Location is required.";
    if (!formData.depreciationMethod) nextErrors.depreciationMethod = "Depreciation method is required.";
    dynamicFields.forEach((field) => {
      if (field.required && !String(categoryFields[field.name] || "").trim()) {
        nextErrors[`category_${field.name}`] = `${field.label} is required.`;
      }
    });
    return nextErrors;
  };

  const buildPayload = () => ({
    ...formData,
    categorySpecificDetails: categoryFields,
  });

  const createAsset = async (resetAfterSave = false) => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix the highlighted asset fields.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/assets`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error(await res.text());

      toast.success("Asset created successfully.");
      if (resetAfterSave) {
        setFormData(initialForm);
        setCategoryFields({});
        setErrors({});
        return;
      }
      navigate("/allassets");
    } catch (err) {
      toast.error(err.message || "Failed to create asset.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <AssetModuleNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="mb-3">
          <h4 className="m-0">
            <i className="bi bi-box-seam me-2"></i> Add Asset
          </h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={() => createAsset(false)} disabled={submitting}>
                {submitting ? "Saving..." : "Save "}
              </button>
              <button className="btn btn-outline-primary btn-sm" onClick={() => createAsset(true)} disabled={submitting}>
                Save & Add Another
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>
            <a href="/allassets" className="text-primary text-decoration-none me-3">
               List
            </a>
          </div>
        </div>

        <Section title="Asset Classification">
          <div className="col-md-3">
            <label className="form-label">Category <span className="text-danger">*</span></label>
            <select name="category" className={`form-select ${errors.category ? "is-invalid" : ""}`} value={formData.category} onChange={handleChange}>
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            {errors.category && <div className="invalid-feedback">{errors.category}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Sub-category <span className="text-danger">*</span></label>
            <select name="subCategory" className={`form-select ${errors.subCategory ? "is-invalid" : ""}`} value={formData.subCategory} onChange={handleChange} disabled={!formData.category}>
              <option value="">Select sub-category</option>
              {dynamicSubCategories.map((sub) => (
                <option key={sub.id} value={sub.name}>{sub.name}</option>
              ))}
            </select>
            {errors.subCategory && <div className="invalid-feedback">{errors.subCategory}</div>}
          </div>
          <div className="col-md-4">
            <label className="form-label">Asset Name <span className="text-danger">*</span></label>
            <input name="assetName" className={`form-control ${errors.assetName ? "is-invalid" : ""}`} value={formData.assetName} onChange={handleChange} placeholder="e.g. Dell Latitude 5520 Laptop" />
            {errors.assetName && <div className="invalid-feedback">{errors.assetName}</div>}
          </div>
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select name="status" className="form-select" value={formData.status} onChange={handleChange}>
              {STATUS_OPTIONS.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </div>
        </Section>

        <Section title="Identification Details">
          <div className="col-md-3">
            <label className="form-label">Brand <span className="text-danger">*</span></label>
            <input name="brand" className={`form-control ${errors.brand ? "is-invalid" : ""}`} value={formData.brand} onChange={handleChange} />
            {errors.brand && <div className="invalid-feedback">{errors.brand}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Model Number <span className="text-danger">*</span></label>
            <input name="modelNumber" className={`form-control ${errors.modelNumber ? "is-invalid" : ""}`} value={formData.modelNumber} onChange={handleChange} />
            {errors.modelNumber && <div className="invalid-feedback">{errors.modelNumber}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Serial Number <span className="text-danger">*</span></label>
            <input name="serialNumber" className={`form-control ${errors.serialNumber ? "is-invalid" : ""}`} value={formData.serialNumber} onChange={handleChange} />
            {errors.serialNumber && <div className="invalid-feedback">{errors.serialNumber}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Colour</label>
            <input name="colour" className="form-control" value={formData.colour} onChange={handleChange} />
          </div>
        </Section>

        <Section title="Procurement & Warranty">
          <div className="col-md-3">
            <label className="form-label">Purchase Date <span className="text-danger">*</span></label>
            <input type="date" name="purchaseDate" className={`form-control ${errors.purchaseDate ? "is-invalid" : ""}`} value={formData.purchaseDate} onChange={handleChange} />
            {errors.purchaseDate && <div className="invalid-feedback">{errors.purchaseDate}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Purchase Cost <span className="text-danger">*</span></label>
            <input type="number" min="0" name="purchaseCost" className={`form-control ${errors.purchaseCost ? "is-invalid" : ""}`} value={formData.purchaseCost} onChange={handleChange} />
            {errors.purchaseCost && <div className="invalid-feedback">{errors.purchaseCost}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Vendor</label>
            <select name="vendorId" className="form-select" value={formData.vendorId} onChange={handleChange}>
              <option value="">Select vendor</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Vendor Name Override</label>
            <input name="vendorName" className="form-control" value={formData.vendorName} onChange={handleChange} placeholder="Optional free-text vendor name" />
          </div>
          <div className="col-md-3">
            <label className="form-label">PO Number</label>
            <input name="poNumber" className="form-control" value={formData.poNumber} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">GRN Number</label>
            <input name="grnNumber" className="form-control" value={formData.grnNumber} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Warranty Start <span className="text-danger">*</span></label>
            <input type="date" name="warrantyStartDate" className={`form-control ${errors.warrantyStartDate ? "is-invalid" : ""}`} value={formData.warrantyStartDate} onChange={handleChange} />
            {errors.warrantyStartDate && <div className="invalid-feedback">{errors.warrantyStartDate}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Warranty End <span className="text-danger">*</span></label>
            <input type="date" name="warrantyEndDate" className={`form-control ${errors.warrantyEndDate ? "is-invalid" : ""}`} value={formData.warrantyEndDate} onChange={handleChange} />
            {errors.warrantyEndDate && <div className="invalid-feedback">{errors.warrantyEndDate}</div>}
          </div>
        </Section>

        <Section title="Ownership & Depreciation">
          <div className="col-md-3">
            <label className="form-label">Location <span className="text-danger">*</span></label>
            <select name="location" className={`form-select ${errors.location ? "is-invalid" : ""}`} value={formData.location} onChange={handleChange}>
              <option value="">Select location</option>
              {LOCATIONS.map((location) => (
                <option key={location}>{location}</option>
              ))}
            </select>
            {errors.location && <div className="invalid-feedback">{errors.location}</div>}
          </div>
          <div className="col-md-3">
            <label className="form-label">Department</label>
            <select name="department" className="form-select" value={formData.department} onChange={handleChange}>
              <option value="">Select department</option>
              {DEPARTMENTS.map((department) => (
                <option key={department}>{department}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Depreciation Method</label>
            <select name="depreciationMethod" className="form-select" value={formData.depreciationMethod} onChange={handleChange}>
              {DEPRECIATION_METHODS.map((method) => (
                <option key={method}>{method}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Useful Life (Years)</label>
            <input type="number" min="0" name="usefulLifeYears" className="form-control" value={formData.usefulLifeYears} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Residual Value</label>
            <input type="number" min="0" name="residualValue" className="form-control" value={formData.residualValue} onChange={handleChange} />
          </div>
          <div className="col-md-9">
            <label className="form-label">Remarks</label>
            <textarea name="remarks" className="form-control" rows={2} value={formData.remarks} onChange={handleChange} placeholder="Condition notes, accessories, or audit remarks..." />
          </div>
        </Section>

        {dynamicFields.length > 0 && (
          <Section title={`${formData.subCategory} Details`}>
            {dynamicFields.map((field) => (
              <div className="col-md-4" key={field.name}>
                <label className="form-label">
                  {field.label}
                  {field.required && <span className="text-danger"> *</span>}
                </label>

                {/* SELECT */}
                {field.type === "select" ? (
                  <select
                    className={`form-select ${errors[`category_${field.name}`] ? "is-invalid" : ""}`}
                    value={categoryFields[field.name] || ""}
                    onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  >
                    <option value="">Select</option>
                    {(field.options || []).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>

                  /* RADIO */
                ) : field.type === "radio" ? (
                  <div className={`border rounded p-2 ${errors[`category_${field.name}`] ? "border-danger" : ""}`}>
                    {(field.options || []).map((option) => (
                      <div className="form-check" key={option}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name={`radio_${field.name}`}
                          id={`radio_${field.name}_${option}`}
                          value={option}
                          checked={categoryFields[field.name] === option}
                          onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        />
                        <label className="form-check-label" htmlFor={`radio_${field.name}_${option}`}>
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>

                  
                  /* CHECKBOX */
                ) : field.type === "checkbox" ? (
                  <div className="form-check mt-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`chk_${field.name}`}
                      checked={!!categoryFields[field.name]}
                      onChange={(e) =>
                        setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.checked }))
                      }
                    />
                    <label className="form-check-label text-muted" htmlFor={`chk_${field.name}`}>
                      Yes
                    </label>
                  </div>

                  /* TEXTAREA */
                ) : field.type === "textarea" ? (
                  <textarea
                    className={`form-control ${errors[`category_${field.name}`] ? "is-invalid" : ""}`}
                    rows={2}
                    value={categoryFields[field.name] || ""}
                    onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  />

                  /* ALL OTHERS: text, number, date, email, phone, url, currency, percentage, datetime-local, time, file */
                ) : (
                  <input
                    type={field.type === "currency" || field.type === "percentage" ? "number" : (field.type || "text")}
                    min={field.type === "currency" || field.type === "percentage" || field.type === "number" ? "0" : undefined}
                    step={field.type === "currency" ? "0.01" : field.type === "percentage" ? "0.1" : undefined}
                    className={`form-control ${errors[`category_${field.name}`] ? "is-invalid" : ""}`}
                    value={categoryFields[field.name] || ""}
                    onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  />
                )}

                {errors[`category_${field.name}`] && (
                  <div className="invalid-feedback d-block">{errors[`category_${field.name}`]}</div>
                )}
              </div>
            ))}
          </Section>
        )}

        <div className="d-flex gap-2 mt-2 mb-4">
          <button className="btn btn-primary" onClick={() => createAsset(false)} disabled={submitting}>
            {submitting ? "Saving..." : "Save "}
          </button>
          <button className="btn btn-outline-primary" onClick={() => createAsset(true)} disabled={submitting}>
            Save & Add Another
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
